import { useState } from "react";
import { useContent } from "../../i18n";
import { formatNumber } from "./format";
import "./style.scss";

type Model = "subscription" | "commission" | "own";

// Illustrative only — the real commission is agreed per provider.
const SAMPLE_COMMISSION = 25;

export const MoneyDemo = () => {
  const { demos } = useContent();
  const t = demos.money;

  const [price, setPrice] = useState(700);
  const [model, setModel] = useState<Model>("commission");
  const [acceptBalance, setAcceptBalance] = useState(true);
  const [balance, setBalance] = useState(100);

  const discount = acceptBalance ? Math.min(balance, price) : 0;
  const memberPays = price - discount;
  const commission = model === "commission" ? Math.min(SAMPLE_COMMISSION, memberPays) : 0;
  const toProvider = memberPays - commission;
  const money = (value: number) => `${formatNumber(value)} ${t.currency}`;
  const share = (value: number) => `${(value / price) * 100}%`;

  return (
    <div className="demo">
      <div className="demo-stage money-stage">
        <div className="money-bar" aria-hidden="true">
          <span className="seg provider" style={{ width: share(toProvider) }} />
          {commission > 0 && <span className="seg medhub" style={{ width: share(commission) }} />}
          {discount > 0 && <span className="seg discount" style={{ width: share(discount) }} />}
        </div>

        <dl className="money-lines">
          <div className="line">
            <dt>{t.memberPays}</dt>
            <dd>{money(memberPays)}</dd>
          </div>
          <div className="line provider">
            <dt>{t.toProvider}</dt>
            <dd>{money(toProvider)}</dd>
          </div>
          {model === "commission" && (
            <div className="line medhub">
              <dt>{t.toMedHub}</dt>
              <dd>{money(commission)}</dd>
            </div>
          )}
          {discount > 0 && (
            <div className="line discount">
              <dt>{t.discount}</dt>
              <dd>{money(discount)}</dd>
            </div>
          )}
        </dl>

        <p className="money-model-note">
          {model === "subscription" && t.subscriptionNote}
          {model === "commission" && t.commissionNote}
          {model === "own" && t.ownNote}
        </p>
      </div>

      <div className="demo-controls">
        <label className="range">
          <span>
            {t.price}: <b>{money(price)}</b>
          </span>
          <input
            type="range"
            min={100}
            max={3000}
            step={50}
            value={price}
            onChange={(e) => setPrice(Number(e.target.value))}
          />
        </label>

        <fieldset>
          <legend>{t.modelLabel}</legend>
          {(Object.keys(t.models) as Model[]).map((key) => (
            <label className="radio" key={key}>
              <input type="radio" name="model" checked={model === key} onChange={() => setModel(key)} />
              <span>{t.models[key]}</span>
            </label>
          ))}
        </fieldset>

        <label className="check">
          <input type="checkbox" checked={acceptBalance} onChange={(e) => setAcceptBalance(e.target.checked)} />
          <span>{t.acceptBalance}</span>
        </label>

        {acceptBalance && (
          <label className="range">
            <span>
              {t.balance}: <b>{money(balance)}</b>
            </span>
            <input
              type="range"
              min={0}
              max={500}
              step={25}
              value={balance}
              onChange={(e) => setBalance(Number(e.target.value))}
            />
          </label>
        )}

        <p className="demo-note">{t.note}</p>
      </div>
    </div>
  );
};
